import { Company } from "@/types/Company";
import { lossesAckQuery, type LossesAck } from "@/lib/legal/dissolveLossesAck";
import { apiDelete, apiGet, apiPut } from "../_apiShim";
const BASE_PATH = "/api/v1/companies/";

// PB-CDP / PB-CDP-7: the doors refuse to delete an organisation that forfeits
// purchased event plans or balances unless the caller acknowledges the loss
// notice (`@QueryParam("acknowledgeLosses")`, absent = false). The ticked
// acknowledgement rides along as `lossesAck*` query params
// (`lossesAckParams`); without one nothing is sent.

// Get a company by ID
export async function getCompany(
  companyId: string,
  fields: string,
): Promise<Company> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";

  return await apiGet<Company>(
    `${BASE_PATH}${companyId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Update a company by ID
export async function updateCompany(
  company: Company,
): Promise<void> {
  await apiPut<void>(`${BASE_PATH}${company.companyId}`, company, {
    "Cache-Control": "no-store",
  });
}

// Request company deletion (sends OTP)
export async function requestCompanyDeletion(
  companyId: string,
  lossesAck: LossesAck | null,
): Promise<void> {
  return await apiDelete<void>(
    `${BASE_PATH}${companyId}/request${lossesAckQuery(lossesAck, "?")}`,
    {
      "Cache-Control": "no-store",
    },
  );
}

/**
 * F24 v5: response shape for {@code DELETE /api/v1/companies/{id}}.
 * Backend returns 202 Accepted with this body so the frontend can
 * switch to the deletion-pending polling screen immediately.
 */
export interface DeleteCompanyResponse {
  status: "pending";
  pollUrl: string;
  retryAfterSeconds: number;
}

// Delete a company by ID (with OTP verification)
export async function deleteCompany(
  companyId: string,
  otp: string,
  // The confirm door re-checks the losses and records the acknowledgement,
  // so it travels again — exactly the request's.
  lossesAck: LossesAck | null,
): Promise<DeleteCompanyResponse> {
  // PR-A4 / H8 — OTP goes in the X-Otp header, NOT the query string,
  // so it doesn't land in nginx access logs / CloudWatch / browser
  // history. Backend was updated to @HeaderParam in parallel.
  return await apiDelete<DeleteCompanyResponse>(
    `${BASE_PATH}${companyId}${lossesAckQuery(lossesAck, "?")}`,
    {
      "Cache-Control": "no-store",
      "X-Otp": otp,
    },
    undefined,
    true, // parse JSON body
  );
}

/**
 * F24 v5: response shape for {@code GET /api/v1/companies/{id}/deletion-status}.
 * Used by the polling screen.
 */
export interface DeletionStatusResponse {
  status: "active" | "pending" | "completed" | "failed";
  attempts?: number;
  retryAfterSeconds?: number;
  reason?: string;
  contactSupport?: boolean;
}

export async function getDeletionStatus(
  companyId: string,
): Promise<DeletionStatusResponse> {
  return await apiGet<DeletionStatusResponse>(
    `${BASE_PATH}${companyId}/deletion-status`,
    {
      "Cache-Control": "no-store",
    },
    true, // parse JSON body
  );
}
